import { QueryKey, useQueryClient, UseQueryOptions } from 'react-query';

import { isAxiosError } from '@app/libs/axios/isAxiosError';

const DELAYABLE_STATUS_CODES = [403, 404];

/**
 * @description
 * 백엔드 MSA 아키텍처에서,
 * 리소스가 생성되고 인가데이터가 생성되는 사이 지연이 있을 수 있습니다.
 * 이런 경우 리소스 조회시 권한이 있어야 하는데 403이 응답될 수 있습니다.
 * 리소스 생성 시 인가데이터까지 생성되었음을 보장하기 위해 사용합니다.
 * @see https://www.notion.so/modusign/Eventual-Consistency-Strong-Consistency-00bc0c35cb8a422f90d4289317c14507
 */
export default function useEnsureQuery() {
  const queryClient = useQueryClient();
  return function ensureQuery<
    TQueryFnData,
    TError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
  >(
    options: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'retry' | 'retryDelay'>
  ) {
    return queryClient.fetchQuery({
      retry: (failureCount, e) => {
        if (isAxiosError(e)) {
          const status = e.response?.status;
          if (
            status &&
            status >= 400 &&
            status < 500 &&
            DELAYABLE_STATUS_CODES.indexOf(status) === -1
          ) {
            return false;
          }
        }
        if (failureCount < 3) {
          return true;
        }
        return false;
      },
      retryDelay: (failureCount) => 2 ** (failureCount - 1) * 100,
      ...options,
    });
  };
}
